import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { logoutLocal } from "@services/auth";
import {
  getIdleTimeoutMs,
  getLastActivityAt,
  markUserActivity,
} from "@utils/sessionActivity";
import { getAppPath, isPublicPath } from "@utils/appPath";

// Module-level guard — survives React StrictMode double-mount / effect remount.
let _isLoggingOut = false;

function hasAuthToken(): boolean {
  return !!localStorage.getItem("access_token");
}

function isTimedOut(): boolean {
  const last = getLastActivityAt();
  if (!last) return false;
  return Date.now() - last >= getIdleTimeoutMs();
}

/** AB#11730: click, key press, and scroll must reset the idle timer. */
export const IDLE_ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  "keydown",
  "pointerdown",
  "click",
  "scroll",
  "wheel",
  "touchstart",
];

export function useSessionIdleTimeout(): void {
  const navigate = useNavigate();
  const lastWriteAtRef = useRef<number>(0);
  const idleCheckTimerRef = useRef<number | null>(null);
  const idleDeadlineTimerRef = useRef<number | null>(null);

  const performIdleLogout = useCallback(() => {
    if (_isLoggingOut) return;
    // Stale deadline timers must not log out after a later activity mark.
    if (!hasAuthToken() || !isTimedOut()) return;
    _isLoggingOut = true;

    // Clear storage first so route guards see no token.
    try { localStorage.clear(); } catch (_) {}
    try { sessionStorage.clear(); } catch (_) {}

    // Fire-and-forget audit cleanup.
    logoutLocal().catch(() => {});

    toast.error("You were signed out due to inactivity. Please sign in again.", {
      autoClose: 2000,
      toastId: "session-expired",
    });

    // Keep public pages publicly accessible, even if a stale/expired token was present.
    const currentPath = getAppPath();
    if (!isPublicPath(currentPath)) {
      navigate("/login", { replace: true });
    }

    // Reset flag after short delay in case navigation doesn't unmount this hook.
    setTimeout(() => { _isLoggingOut = false; }, 3000);
  }, [navigate]);

  useEffect(() => {
    const clearDeadlineTimer = () => {
      if (idleDeadlineTimerRef.current !== null) {
        window.clearTimeout(idleDeadlineTimerRef.current);
        idleDeadlineTimerRef.current = null;
      }
    };

    const scheduleDeadlineTimer = () => {
      clearDeadlineTimer();
      if (!hasAuthToken()) return;

      const now = Date.now();
      const last = getLastActivityAt() ?? now;
      const timeoutMs = getIdleTimeoutMs();
      const remaining = timeoutMs - (now - last);

      if (remaining <= 0) {
        performIdleLogout();
        return;
      }

      idleDeadlineTimerRef.current = window.setTimeout(() => {
        // Re-check so a recent activity mark is not raced by a stale timer.
        if (isTimedOut()) performIdleLogout();
        else scheduleDeadlineTimer();
      }, remaining + 100);
    };

    const writeThrottled = (now: number) => {
      if (now - lastWriteAtRef.current < 5_000) return;
      lastWriteAtRef.current = now;
      markUserActivity(now);
      scheduleDeadlineTimer();
    };

    const handleAnyActivity = (event: Event) => {
      if (!hasAuthToken()) return;
      if (!event.isTrusted) return;
      // If already timed out, don't let stray input reset the clock.
      if (isTimedOut()) {
        performIdleLogout();
        return;
      }
      writeThrottled(Date.now());
    };

    for (const evt of IDLE_ACTIVITY_EVENTS) {
      window.addEventListener(evt, handleAnyActivity, { capture: true, passive: true });
    }
    window.addEventListener("session-activity", scheduleDeadlineTimer);

    // Seed last-activity on mount if missing.
    if (hasAuthToken()) {
      if (!getLastActivityAt()) markUserActivity(Date.now());
      scheduleDeadlineTimer();
    }

    // Periodic fallback check (cross-tab safety).
    const checkIdle = () => {
      if (!hasAuthToken()) return;

      if (!getLastActivityAt()) {
        markUserActivity(Date.now());
        scheduleDeadlineTimer();
        return;
      }

      if (isTimedOut()) performIdleLogout();
    };

    const interval = Math.min(Math.max(Math.floor(getIdleTimeoutMs() / 6), 15_000), 60_000);
    idleCheckTimerRef.current = window.setInterval(checkIdle, interval);

    return () => {
      for (const evt of IDLE_ACTIVITY_EVENTS) {
        window.removeEventListener(evt, handleAnyActivity, { capture: true } as any);
      }
      window.removeEventListener("session-activity", scheduleDeadlineTimer);
      if (idleCheckTimerRef.current !== null) window.clearInterval(idleCheckTimerRef.current);
      clearDeadlineTimer();
    };
  }, [performIdleLogout]);
}
